// File path: /modules/navigate/frontend/src/module.ts
// Navigate (MTSS) Module Entry Point for Shell App

import React from 'react';
import {
  HomeIcon,
  UsersIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentListIcon,
  ChatBubbleLeftRightIcon,
  AcademicCapIcon,
  ChartBarIcon,
  BellAlertIcon,
} from '@heroicons/react/24/outline';
import type { ModuleEntryPoint, ModuleRouteConfig, ModuleNavigationItem } from '@core/types/module';

// Lazy-loaded page components
const CoordinatorDashboard = React.lazy(() => import('./pages/coordinator/CoordinatorDashboard'));
const CounselorDashboard = React.lazy(() => import('./pages/counselor/CounselorDashboard'));
const TeacherStudentsPage = React.lazy(() => import('./pages/teacher/TeacherStudentsPage'));
const StudentListPage = React.lazy(() => import('./pages/common/StudentListPage'));
const StudentProfilePage = React.lazy(() => import('./pages/common/StudentProfilePage'));
const InterventionLibraryPage = React.lazy(() => import('./pages/common/InterventionLibraryPage'));
const AlertsPage = React.lazy(() => import('./pages/common/AlertsPage'));
const TouchpointLogPage = React.lazy(() => import('./pages/common/TouchpointLogPage'));

// Placeholder pages for future implementation
const ProgressMonitoringPage = React.lazy(() => Promise.resolve({
  default: () => React.createElement('div', { className: 'max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-8' },
    React.createElement('h1', { className: 'text-2xl font-semibold text-gray-900' }, 'Progress Monitoring'),
    React.createElement('p', { className: 'mt-2 text-gray-600' }, 'Track intervention effectiveness and student progress.')
  )
}));

const ReportsPage = React.lazy(() => Promise.resolve({
  default: () => React.createElement('div', { className: 'max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-8' },
    React.createElement('h1', { className: 'text-2xl font-semibold text-gray-900' }, 'Reports'),
    React.createElement('p', { className: 'mt-2 text-gray-600' }, 'MTSS program reports and analytics.')
  )
}));

// Role definitions for access control
const TEACHER_ROLES = ['Teacher', 'Coach', 'Counselor', 'MTSSCoordinator', 'SchoolAdmin', 'DistrictAdmin', 'SuperAdmin'];
const COUNSELOR_ROLES = ['Counselor', 'MTSSCoordinator', 'SchoolAdmin', 'DistrictAdmin', 'SuperAdmin'];
const COORDINATOR_ROLES = ['MTSSCoordinator', 'SchoolAdmin', 'DistrictAdmin', 'SuperAdmin'];
const ADMIN_ROLES = ['SchoolAdmin', 'DistrictAdmin', 'SuperAdmin'];

/**
 * All routes for the Navigate module
 * Paths are relative to the module base path (/navigate)
 */
const routes: ModuleRouteConfig[] = [
  // Dashboard routes by role
  { path: 'coordinator', element: CoordinatorDashboard, allowedRoles: COORDINATOR_ROLES },
  { path: 'counselor', element: CounselorDashboard, allowedRoles: COUNSELOR_ROLES },
  { path: 'teacher', element: TeacherStudentsPage, allowedRoles: TEACHER_ROLES },

  // Student routes
  { path: 'students', element: StudentListPage, allowedRoles: COUNSELOR_ROLES },
  { path: 'students/:studentId', element: StudentProfilePage, allowedRoles: TEACHER_ROLES },
  { path: 'my-students', element: TeacherStudentsPage, allowedRoles: TEACHER_ROLES },

  // Intervention routes
  { path: 'interventions', element: InterventionLibraryPage, allowedRoles: COUNSELOR_ROLES },
  { path: 'interventions/library', element: InterventionLibraryPage, allowedRoles: COUNSELOR_ROLES },

  // Alert routes
  { path: 'alerts', element: AlertsPage, allowedRoles: COUNSELOR_ROLES },
  { path: 'alerts/my', element: AlertsPage, allowedRoles: COUNSELOR_ROLES },

  // Touchpoint routes
  { path: 'touchpoints', element: TouchpointLogPage, allowedRoles: TEACHER_ROLES },
  { path: 'touchpoints/new', element: TouchpointLogPage, allowedRoles: TEACHER_ROLES },
  { path: 'students/:studentId/touchpoints', element: TouchpointLogPage, allowedRoles: TEACHER_ROLES },

  // Progress monitoring routes
  { path: 'progress', element: ProgressMonitoringPage, allowedRoles: COUNSELOR_ROLES },
  { path: 'students/:studentId/progress', element: ProgressMonitoringPage, allowedRoles: TEACHER_ROLES },

  // Reports routes
  { path: 'reports', element: ReportsPage, allowedRoles: ADMIN_ROLES },
  { path: 'reports/tier-distribution', element: ReportsPage, allowedRoles: COORDINATOR_ROLES },
  { path: 'reports/intervention-effectiveness', element: ReportsPage, allowedRoles: COORDINATOR_ROLES },
];

/**
 * Get navigation items based on user role
 */
function getNavigationItems(userRole: string): ModuleNavigationItem[] {
  const normalizedRole = userRole.toLowerCase();

  // Get dashboard path based on role
  const getDashboardPath = () => {
    switch (normalizedRole) {
      case 'mtsscoordinator':
      case 'schooladmin':
      case 'districtadmin':
      case 'superadmin':
        return 'coordinator';
      case 'counselor':
        return 'counselor';
      case 'coach':
      case 'teacher':
      default:
        return 'teacher';
    }
  };

  const baseItems: ModuleNavigationItem[] = [
    { name: 'Dashboard', href: getDashboardPath(), icon: HomeIcon, roles: TEACHER_ROLES },
  ];

  switch (normalizedRole) {
    case 'teacher':
    case 'coach':
      return [
        ...baseItems,
        { name: 'My Students', href: 'my-students', icon: UsersIcon, roles: TEACHER_ROLES },
        { name: 'Touchpoints', href: 'touchpoints', icon: ChatBubbleLeftRightIcon, roles: TEACHER_ROLES },
      ];

    case 'counselor':
      return [
        ...baseItems,
        { name: 'All Students', href: 'students', icon: UsersIcon, roles: COUNSELOR_ROLES },
        { name: 'Alerts', href: 'alerts', icon: BellAlertIcon, roles: COUNSELOR_ROLES },
        { name: 'Interventions', href: 'interventions', icon: ClipboardDocumentListIcon, roles: COUNSELOR_ROLES },
        { name: 'Touchpoints', href: 'touchpoints', icon: ChatBubbleLeftRightIcon, roles: COUNSELOR_ROLES },
        { name: 'Progress', href: 'progress', icon: ChartBarIcon, roles: COUNSELOR_ROLES },
      ];

    case 'mtsscoordinator':
      return [
        ...baseItems,
        { name: 'All Students', href: 'students', icon: UsersIcon, roles: COORDINATOR_ROLES },
        { name: 'Alerts', href: 'alerts', icon: BellAlertIcon, roles: COORDINATOR_ROLES },
        { name: 'Intervention Library', href: 'interventions/library', icon: AcademicCapIcon, roles: COORDINATOR_ROLES },
        { name: 'Progress Monitoring', href: 'progress', icon: ChartBarIcon, roles: COORDINATOR_ROLES },
        { name: 'Reports', href: 'reports', icon: ClipboardDocumentListIcon, roles: COORDINATOR_ROLES },
      ];

    case 'schooladmin':
    case 'districtadmin':
    case 'superadmin':
      return [
        ...baseItems,
        { name: 'All Students', href: 'students', icon: UsersIcon, roles: ADMIN_ROLES },
        { name: 'Alerts', href: 'alerts', icon: BellAlertIcon, roles: ADMIN_ROLES },
        { name: 'Intervention Library', href: 'interventions/library', icon: AcademicCapIcon, roles: ADMIN_ROLES },
        { name: 'Progress Monitoring', href: 'progress', icon: ChartBarIcon, roles: ADMIN_ROLES },
        { name: 'Reports', href: 'reports', icon: ClipboardDocumentListIcon, roles: ADMIN_ROLES },
      ];

    default:
      return baseItems;
  }
}

/**
 * Get the default route for a user based on their role
 */
function getDefaultRoute(userRole: string): string {
  const normalizedRole = userRole.toLowerCase();
  switch (normalizedRole) {
    case 'mtsscoordinator':
    case 'schooladmin':
    case 'districtadmin':
    case 'superadmin':
      return 'coordinator';
    case 'counselor':
      return 'counselor';
    case 'coach':
    case 'teacher':
    default:
      return 'teacher';
  }
}

/**
 * Navigate (MTSS) module entry point
 */
const NavigateModule: ModuleEntryPoint = {
  moduleId: 'navigate',
  routes,
  getNavigationItems,
  getDefaultRoute,
};

export default NavigateModule;
export { getDefaultRoute, getNavigationItems, routes };
